// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — Solar System mode in a headset or a phone's AR (owner,
 * 2026-11-04; docs/AYNI_XR_SOLAR_SYSTEM_PLAN.md). The XR session's
 * presenter for the mode (ui/solarSystemMode.ts): the same orrery as the
 * browser's (solarSystemScene.ts), drawn in the room.
 *
 * - **Overview**: the Sun stands where the globe stood, the planets' plane
 *   0.45 m under the globe's centre, so the viewer looks down on it with
 *   the outer orbits round them; at true distances Pluto's orbit is 5 m
 *   across. The left stick's size scales it as it scales the globe. An
 *   opaque sky of stars (no Sun of its own) hides the room, as Fully VR
 *   does, so the orrery floats in space.
 * - **Fades**: a black sphere round the eyes, so nothing moves while the
 *   view changes.
 * - **Flight**: the viewer stays still and space moves: the arriving body
 *   comes straight at them and stops where the globe is, as large, then
 *   the globe with the body's dataset takes its place.
 *
 * The session (vrSession.ts) calls {@link VrSolarHandle.frame} last in
 * each frame, hides the globe while {@link VrSolarHandle.active}, and
 * offers rays to {@link VrSolarHandle.pick} (a trigger on a body visits it).
 */

import type * as THREE from 'three'
import { createVrNightSky, type VrNightSkyHandle } from '../vrNightSky'
import type { SolarPresenter } from './presenter'
import type { SolarBodyId } from './bodies'
import { flightSeconds } from './flight'
import type { OrreryLayout } from './layout'
import { createSolarSystemScene, type SolarSceneHandle } from './solarSystemScene'

const FADE_MS = 450
/** The planets' plane under the globe's centre, metres. */
const PLANE_DROP_M = 0.45
/** A marker is easier to hit with a ray than its size suggests, radians (~2.5°; a phone's finger 4°). */
const PICK_TOLERANCE = 0.044
const PICK_TOLERANCE_TOUCH = 0.07

export interface VrSolarOptions {
  scene: THREE.Scene
  camera: THREE.Camera
  /** The primary globe (its place and size are the orrery's anchor). */
  globe: THREE.Object3D
  /** The globe's radius as drawn now, world metres. */
  globeRadius(): number
  nameOf(id: SolarBodyId): string
  pictureUrl(id: SolarBodyId): string | null
  /** A trigger (or a tap) on a body. */
  onPick(id: SolarBodyId): void
  /** A headset asks the comfort question on its panel; a phone shows the page's dialog in its overlay. */
  asksInScene: boolean
  handheld: boolean
}

export interface VrSolarHandle {
  readonly presenter: SolarPresenter
  /** The orrery, a fade or a flight has the room: the globe and its parts step aside. */
  readonly active: boolean
  readonly flying: boolean
  readonly scene: SolarSceneHandle
  /** Per frame, last before drawing. */
  frame(dtSeconds: number, viewportHeightPx: number, pixelsPerRadian: number): void
  /** A ray's body, or null. A hit visits it (through onPick) and returns true. */
  tap(origin: THREE.Vector3, direction: THREE.Vector3): boolean
  /** The body under a ray (hover), or null. */
  hover(origin: THREE.Vector3 | null, direction: THREE.Vector3 | null): void
  dispose(): void
}

export function createVrSolarSystem(THREE_: typeof THREE, opts: VrSolarOptions): VrSolarHandle {
  const scene = createSolarSystemScene(THREE_, { nameOf: opts.nameOf, pictureUrl: opts.pictureUrl })
  scene.root.visible = false
  opts.scene.add(scene.root)

  // The sky: built on first use (a 4K picture is not loaded for a session that never opens the orrery).
  const skyRoot = new THREE_.Group()
  skyRoot.name = 'solar-sky'
  opts.scene.add(skyRoot)
  let sky: VrNightSkyHandle | null = null

  // The fade: a black sphere round the eyes, drawn over everything.
  const fadeMat = new THREE_.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0, side: THREE_.BackSide, depthTest: false, depthWrite: false })
  const fadeMesh = new THREE_.Mesh(new THREE_.SphereGeometry(0.3, 24, 12), fadeMat)
  fadeMesh.name = 'solar-fade'
  fadeMesh.renderOrder = 10_000
  fadeMesh.frustumCulled = false
  fadeMesh.visible = false
  opts.scene.add(fadeMesh)

  let shown = false
  let playing = false
  let rate = 1
  let fade: null | { from: number; to: number; start: number; ms: number; resolve: () => void } = null
  let fadeLevel = 0
  let flight: null | { start: number; seconds: number; distanceKm: number; resolve: (v: { distanceKm: number; seconds: number }) => void } = null
  /** The globe's scale when the orrery came up: the left stick's size after it scales the orrery. */
  let baseScale = 1
  /** Where the globe stood when the orrery came up: the Sun stays there (the hidden globe may be moved meanwhile). */
  const anchor = new THREE_.Vector3()
  const tmpV = new THREE_.Vector3()
  const tmpQ = new THREE_.Quaternion()
  const camPos = new THREE_.Vector3()

  function place(): void {
    tmpV.copy(anchor)
    const scale = Math.max(0.05, opts.globe.scale.x / baseScale)
    tmpV.y -= PLANE_DROP_M * Math.min(1, scale)
    scene.setPlacement(tmpV, tmpQ.identity(), scale)
  }

  function ensureSky(): VrNightSkyHandle {
    sky ??= createVrNightSky(THREE_, skyRoot, { sun: false })
    return sky
  }

  const presenter: SolarPresenter = {
    get layout(): OrreryLayout {
      return scene.layout
    },
    get timeMs() {
      return scene.timeMs
    },
    get playing() {
      return playing
    },
    get rate() {
      return rate
    },
    asksInScene: opts.asksInScene,
    scene,

    show() {
      shown = true
      baseScale = opts.globe.scale.x || 1
      opts.globe.getWorldPosition(anchor)
      place()
      scene.root.visible = true
      ensureSky()
    },

    hide() {
      shown = false
      playing = false
      if (!flight) scene.root.visible = false
    },

    setLayout(layout) {
      scene.setLayout(layout, true)
    },

    setTime(ms) {
      scene.setTime(ms)
    },

    setPlaying(on) {
      playing = on
    },

    setRate(days) {
      rate = days
    },

    fade(toBlack, ms = FADE_MS) {
      return new Promise((resolve) => {
        fade?.resolve()
        fade = { from: fadeLevel, to: toBlack ? 1 : 0, start: performance.now(), ms, resolve }
      })
    },

    fly(f) {
      opts.camera.getWorldPosition(camPos)
      opts.globe.getWorldPosition(tmpV)
      const ahead = tmpV.clone().sub(camPos)
      const distance = Math.max(0.3, ahead.length())
      ahead.normalize()
      const distanceKm = scene.beginFlight({
        from: f.from,
        to: f.to,
        timeMs: scene.timeMs,
        viewer: camPos.clone(),
        ahead,
        up: new THREE_.Vector3(0, 1, 0),
        arrivalDistanceM: distance,
        arrivalRadiusM: Math.max(0.02, opts.globeRadius()),
      })
      const seconds = flightSeconds(distanceKm)
      playing = false
      scene.root.visible = true
      ensureSky()
      return new Promise((resolve) => {
        flight = { start: performance.now() + FADE_MS * 0.4, seconds, distanceKm, resolve }
        void presenter.fade(false)
      })
    },

    async reveal() {
      // Through black, so the globe takes the arriving body's place unseen.
      await presenter.fade(true, 260)
      scene.endFlight()
      if (!shown) scene.root.visible = false
      await presenter.fade(false, 300)
    },

    relabel() {
      scene.relabel()
    },
  }

  return {
    presenter,
    scene,
    get active() {
      return shown || !!flight || scene.flying
    },
    get flying() {
      return !!flight || scene.flying
    },

    frame(dt, viewportHeightPx, ppr) {
      const now = performance.now()
      opts.camera.getWorldPosition(camPos)
      if (fade) {
        const u = Math.min(1, (now - fade.start) / fade.ms)
        fadeLevel = fade.from + (fade.to - fade.from) * u
        if (u >= 1) {
          const done = fade
          fade = null
          done.resolve()
        }
      }
      fadeMat.opacity = fadeLevel
      fadeMesh.visible = fadeLevel > 0.001
      fadeMesh.position.copy(camPos)

      if (flight) {
        const u = Math.min(1, Math.max(0, (now - flight.start) / 1000 / flight.seconds))
        scene.setFlightProgress(u)
        if (u >= 1) {
          const done = flight
          flight = null
          done.resolve({ distanceKm: done.distanceKm, seconds: done.seconds })
        }
      } else if (shown) {
        if (playing) scene.setTime(scene.timeMs + dt * rate * 86_400_000)
        place()
      }

      const on = scene.root.visible
      if (on) scene.update(camPos, ppr, dt)
      if (sky) {
        skyRoot.position.copy(camPos)
        sky.update(on, on ? scene.celestialToWorld() : null, scene.timeMs, viewportHeightPx)
      }
    },

    tap(origin, direction) {
      if (!shown || flight) return false
      const id = scene.pick(origin, direction, opts.handheld ? PICK_TOLERANCE_TOUCH : PICK_TOLERANCE)
      if (!id) return false
      opts.onPick(id)
      return true
    },

    hover(origin, direction) {
      if (!shown || flight || !origin || !direction) return scene.setHover(null)
      scene.setHover(scene.pick(origin, direction, PICK_TOLERANCE))
    },

    dispose() {
      fade?.resolve()
      flight?.resolve({ distanceKm: flight.distanceKm, seconds: flight.seconds })
      sky?.dispose()
      skyRoot.removeFromParent()
      fadeMesh.geometry.dispose()
      fadeMat.dispose()
      fadeMesh.removeFromParent()
      scene.dispose()
    },
  }
}
